import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, MessageCircle, Mail, BookOpen } from "lucide-react";
import { SITE_EMAILS } from "../lib/siteConfig";

// Help center — static FAQ grouped into 4 categories, each with collapsible Q/A.
// Kept in one file (no DB) so the page is fast and easy to edit.
const FAQ = [
  {
    category: "Booking",
    items: [
      {
        q: "How do I book a vehicle?",
        a: "Search by city and dates, pick a car, then tap Book Now on its detail page. We'll confirm availability and run a quick identity check before locking your trip.",
      },
      {
        q: "Can I cancel a booking?",
        a: "Yes. Cancel at least 24 hours before pick-up for the maximum refund, less the applicable platform retention and any secured Airalo SIM cost. Cancellations 6–24 hours before pick-up refund 50% of base rental plus taxes and any one-way or after-hours fees; inside 6 hours, the booking is non-refundable.",
        link: "/cancellation",
      },
      {
        q: "What's the minimum and maximum trip length?",
        a: "Trips can be as short as 6 hours and as long as 30 days. For longer rentals, contact the host directly through the message thread.",
      },
      {
        q: "How does the deposit work?",
        a: "Funds are held in escrow when you confirm, and released to the host 24 hours after a successful return. Damages, late returns, or fines are deducted automatically.",
      },
    ],
  },
  {
    category: "Identity & safety",
    items: [
      {
        q: "Why do I need to verify my identity?",
        a: "We verify every renter and host with a government ID + live selfie match. This protects everyone in the marketplace and is required by our insurance partners.",
      },
      {
        q: "How long does verification take?",
        a: "Typically under 30 seconds. If our system needs a manual review, an operator gets back to you within 2 business hours.",
      },
      {
        q: "My verification failed — what should I do?",
        a: "Retry with a well-lit photo of your physical ID (not a screen photo) and a clear live selfie. If it still fails, email support with your account email — we can review manually.",
      },
      {
        q: "What is the minimum age to rent?",
        a: "You must be at least 24 years old and hold a valid driver's licence. Your date of birth is taken from your verified licence or profile settings and checked before booking.",
      },
      {
        q: "Is my ID stored securely?",
        a: "Yes. ID images are encrypted at rest, never shared with hosts, and automatically purged 90 days after your last trip.",
      },
    ],
  },
  {
    category: "Insurance & damage",
    items: [
      {
        q: "Am I covered during a trip?",
        a: "Every confirmed booking includes a per-trip protection plan covering third-party liability and collision damage to the rented vehicle, subject to a deductible. Coverage limits and deductibles vary by region — contact support for the details that apply to your trip.",
      },
      {
        q: "What if there's an accident?",
        a: "Make sure everyone is safe, call local emergency services, then report the incident in-app within 24 hours. Our support team will guide you through the claim.",
      },
      {
        q: "Are there any restrictions?",
        a: "No off-road driving, no commercial ride-sharing, and no driving outside the listed coverage region without prior host approval.",
      },
    ],
  },
  {
    category: "Hosting & payouts",
    items: [
      {
        q: "How much can I earn as a host?",
        a: "Hosts keep 82% of trip revenue. Earnings depend on vehicle class, location, and utilization — premium SUVs in major metros typically clear $1,800–$3,500/month.",
      },
      {
        q: "When do I get paid?",
        a: "Weekly. Payouts settle every Tuesday for trips that completed the prior week, directly to your linked bank account.",
      },
      {
        q: "What if my vehicle is damaged?",
        a: "Our protection plan covers eligible damage up to $50K. File a claim in-app within 48 hours of trip end with photos and an inspection report.",
      },
    ],
  },
];

const INSURANCE_SYNONYMS = [
  "insurance", "insured", "coverage", "cover", "policy", "claim",
  "damage", "accident", "deductible", "liability", "collision", "protection",
];

const CATEGORY_TAGS = {
  Booking: ["book", "booking", "cancel", "cancellation", "deposit", "trip", "rental", "refund", "escrow"],
  "Identity & safety": ["identity", "verify", "verification", "kyc", "id", "selfie", "licence", "license", "age", "safety"],
  "Insurance & damage": INSURANCE_SYNONYMS,
  "Hosting & payouts": ["host", "hosting", "payout", "earn", "paid", "bank", "weekly", "listing"],
};

function faqSearchScore(category, item, query) {
  const q = query.trim().toLowerCase();
  if (!q) return 1;
  let score = 0;
  const catLower = category.toLowerCase();
  if (catLower.includes(q)) score += 50;
  (CATEGORY_TAGS[category] || []).forEach((tag) => {
    if (tag.includes(q) || q.includes(tag)) score += 28;
  });
  if (item.q.toLowerCase().includes(q)) score += 22;
  if (item.a.toLowerCase().includes(q)) score += 12;
  if (category === "Insurance & damage" && INSURANCE_SYNONYMS.some((s) => q.includes(s) || s.includes(q))) {
    score += 120;
  }
  return score;
}

function FAQItem({ q, a, link }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[var(--zc-border)] rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-[var(--zc-bg-3)] transition"
        data-testid={`faq-q-${q.slice(0, 20).toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span className="text-sm font-semibold text-[var(--zc-text)]">{q}</span>
        <ChevronDown
          size={16}
          className={`text-[var(--zc-text-3)] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[var(--zc-text-2)] leading-relaxed border-t border-[var(--zc-border)] pt-4">
          {a}
          {link && (
            <p className="mt-3">
              <Link to={link} className="text-[var(--zc-accent)] font-semibold">Read full policy →</Link>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [query, setQuery] = useState("");
  const filtered = FAQ.map((cat) => ({
    ...cat,
    items: cat.items
      .map((it) => ({ ...it, _score: faqSearchScore(cat.category, it, query) }))
      .filter((it) => !query.trim() || it._score > 0)
      .sort((a, b) => b._score - a._score),
  }))
    .filter((cat) => cat.items.length)
    .sort((a, b) => {
      const aMax = Math.max(...a.items.map((it) => it._score));
      const bMax = Math.max(...b.items.map((it) => it._score));
      return bMax - aMax;
    });

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="help-page">
      <div className="eyebrow mb-3">Help center</div>
      <h1 className="zc-headline text-5xl md:text-6xl tracking-tight">
        Answers in under a minute.
      </h1>
      <p className="text-[var(--zc-text-2)] mt-4 max-w-2xl">
        Common questions about booking, identity verification, insurance, hosting, and payouts.
      </p>

      <div className="relative mt-10 max-w-xl">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--zc-text-3)]" />
        <input
          type="text"
          aria-label="Search help articles"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search e.g. 'cancel', 'insurance', 'payout'…"
          className="zc-input h-12 w-full pl-11 pr-4 text-sm"
          data-testid="help-search"
        />
      </div>

      <div className="mt-12 space-y-12">
        {filtered.map((cat) => (
          <div key={cat.category}>
            <div className="flex items-center gap-3 mb-5">
              <div className="eyebrow text-[var(--zc-text-3)]">
                {cat.category}
              </div>
              <div className="h-px flex-1 bg-[var(--zc-border)]" />
            </div>
            <div className="space-y-3">
              {cat.items.map((it) => (
                <FAQItem key={it.q} q={it.q} a={it.a} link={it.link} />
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-[var(--zc-text-3)] text-sm">
            No matching answers. Try a different search or contact support below.
          </div>
        )}
      </div>

      <div className="mt-20 grid md:grid-cols-3 gap-4">
        <ContactCard
          icon={<MessageCircle size={20} />}
          title="Trip messages"
          body="Message your host or renter from an active booking in My bookings."
          cta="My bookings"
          to="/profile/bookings"
        />
        <ContactCard
          icon={<Mail size={20} />}
          title="Email support"
          body="Average response time under 4 hours, 7 days a week."
          cta={SITE_EMAILS.support}
          to={`mailto:${SITE_EMAILS.support}`}
          external
        />
        <ContactCard
          icon={<BookOpen size={20} />}
          title="Safety center"
          body="Insurance coverage and accident reporting guidance."
          cta="Read the guide"
          to="/safety"
        />
      </div>
    </div>
  );
}

function ContactCard({ icon, title, body, cta, to, external }) {
  const Wrap = external ? "a" : Link;
  const props = external ? { href: to } : { to };
  return (
    <div className="zc-card p-6">
      <div className="w-10 h-10 grid place-items-center rounded-lg" style={{ background: "var(--zc-secondary-soft)", color: "var(--zc-secondary)" }}>
        {icon}
      </div>
      <div className="zc-title text-lg mt-4">{title}</div>
      <p className="text-sm text-[var(--zc-text-2)] mt-2 leading-relaxed">{body}</p>
      <Wrap {...props} className="text-sm font-semibold text-[var(--zc-secondary)] mt-4 inline-block hover:underline">
        {cta} →
      </Wrap>
    </div>
  );
}
